import { useEffect } from 'react'
import { useAttachmentStore } from '@renderer/chat'
import type { PendingAttachmentStagedAction } from '@renderer/chat/types/attachments'
import type { ActionViewModel } from './use-action-view-model'

/**
 * Scoped chat: while the Action View is open, the draft rides along as an
 * attachment pill on the shared chat input (mirrors `useMeetingChatAttachment`).
 * Re-attached when the draft's view changes so the pill tracks regenerations.
 */
export function useActionChatAttachment(model: ActionViewModel) {
  const { addAttachment, removeAttachments } = useAttachmentStore()
  const action = model.action

  const actionId = action?.id
  const view = action?.view

  useEffect(() => {
    if (!action || !actionId) {
      return
    }

    const pending: PendingAttachmentStagedAction = {
      type: 'stagedAction',
      actionId,
      title: action.view?.title || action.title,
      actionType: action.actionType ?? '',
      toolSet: action.toolSet,
      view: action.view
        ? {
            title: action.view.title,
            icon: action.view.icon,
            content: action.view.content,
            tags: action.view.tags,
            invokeLabel: action.view.invokeLabel,
          }
        : undefined,
    }

    // Drop any prior pill for this action so a regenerated draft replaces it.
    removeAttachments((attachment) => {
      if (attachment.pending?.type !== 'stagedAction') {
        return true
      }
      return attachment.pending.actionId !== actionId
    })
    addAttachment({ pending })

    return () => {
      removeAttachments((attachment) => {
        if (attachment.pending?.type !== 'stagedAction') {
          return true
        }
        return attachment.pending.actionId !== actionId
      })
    }
    // Reattach only when the identity or rendered view changes, not on every
    // refetched object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, view, addAttachment, removeAttachments])
}
